import { marked } from 'marked';
import { CONTENT_CONFIG } from '../config';

/**
 * Convert markdown text to HTML
 * @param markdown - Markdown string to convert
 * @returns HTML string
 */
export function markdownToHtml(markdown: string): string {
  return marked.parse(markdown);
}

/**
 * Build JSON-LD script content for structured data
 * @param json - Object to serialize as JSON-LD
 * @returns Formatted JSON string
 */
export function buildJsonLdScript(json: Record<string, unknown>): string {
  return JSON.stringify(json, null, 2);
}

/**
 * Extract a text excerpt from markdown content
 * @param markdown - Markdown string to excerpt
 * @param maxLength - Maximum length of excerpt (default from config)
 * @returns Plain text excerpt with ellipsis if truncated
 */
export function toExcerpt(markdown: string, maxLength = CONTENT_CONFIG.EXCERPT_MAX_LENGTH): string {
  // Remove HTML comments (frontmatter) first
  const withoutComments = markdown.replace(/<!--[\s\S]*?-->/g, '');
  const plain = withoutComments
    .replace(/[#>*_`~-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= maxLength) return plain;
  return `${plain.slice(0, maxLength - 3)}...`;
}

/**
 * Decode HTML entities for display
 * Converts &lt; to <, &gt; to >, &amp; to &, etc.
 * Uses native browser API on client-side, regex replacement on server-side
 * @param html - HTML string with encoded entities
 * @returns Decoded HTML string
 */
export function decodeHtml(html: string): string {
  // Server-side: use simple string replacement
  if (typeof document === 'undefined') {
    return html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
  }
  
  // Client-side: use textarea trick
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}
